import React, { FC, Suspense, lazy } from 'react';

import { CssBaseline, ThemeProvider, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import ProjectSwitchOverlay from 'components/common/ProjectSwitchOverlay';
import PromoBanner from 'components/common/PromoBanner';
import AdaptiveLayoutShell from 'components/layout/shells/AdaptiveLayoutShell';
import { isLeadnamicsDomain } from 'hooks/useBrand';
import useCurrentRoute from 'hooks/useCurrentRoute';
import useCurrentUser from 'hooks/useCurrentUser';
import usePromoBanner from 'hooks/usePromoBanner';
import usePushNotificationNavigation from 'hooks/usePushNotificationNavigation';
import useSessionSync from 'hooks/useSessionSync';
import useSidebarCollapsed from 'hooks/useSidebarCollapsed';

import styles from './Layout.module.scss';
import DefaultTheme from './themes/DefaultTheme';

const THEMES: Theme[] = [DefaultTheme];
const NotificationCenter = lazy(() => import('components/notifications/NotificationCenter'));
const setThemeXLTypographySize = (theme: Theme): void => {
  // eslint-disable-next-line no-param-reassign
  theme.typography.h2[theme.breakpoints.up('xl')] = {
    fontSize: '26px',
  };
  // eslint-disable-next-line no-param-reassign
  theme.typography.h3[theme.breakpoints.up('xl')] = {
    fontSize: '22px',
  };
  // eslint-disable-next-line no-param-reassign
  theme.typography.h4[theme.breakpoints.up('xl')] = {
    fontSize: '21px',
  };
};

THEMES.forEach(setThemeXLTypographySize);

interface Props {
  children?: JSX.Element;
}

const Layout: FC<Props> = ({ children = undefined }: Props) => {
  const { private: privateRoute, noLayout, path, title, mobileTitle } = useCurrentRoute();
  const { isBannerVisible, dismissBanner, config } = usePromoBanner();
  const { isCollapsed, toggleCollapsed } = useSidebarCollapsed();
  const currentUser = useCurrentUser();
  const isAuthenticated = Boolean(currentUser?.accessToken);

  const { pendingSwitch, confirmSwitch } = useSessionSync(currentUser);
  const switchOverlay = pendingSwitch ? <ProjectSwitchOverlay onConfirm={confirmSwitch} /> : null;

  usePushNotificationNavigation();

  // Brand detection is handled in index.html on initial page load
  // No need to duplicate it here

  const theme = THEMES[0];
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Routes that need full-width without padding
  const isFullWidthRoute = path === '/client-management' || path === '/funnel' || path === '/leads';
  const isFunnelRoute = path.startsWith('/funnel');
  const isLoginRoute =
    path === '/sign-in' ||
    path === '/forgot-password' ||
    path === '/reset-password' ||
    path === '/activate' ||
    path === '/';

  React.useEffect(() => {
    if (isLeadnamicsDomain()) return undefined;
    document.body.classList.toggle('cookiefirst-funnel-offset', isFunnelRoute);

    return () => {
      document.body.classList.remove('cookiefirst-funnel-offset');
    };
  }, [isFunnelRoute]);

  React.useEffect(() => {
    if (isLeadnamicsDomain()) return undefined;
    document.body.classList.toggle('cookiefirst-login', isLoginRoute);

    return () => {
      document.body.classList.remove('cookiefirst-login');
    };
  }, [isLoginRoute]);

  if (!privateRoute || noLayout || !isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className={styles.ComponentContainer}>{children}</div>
        {switchOverlay}
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', width: '100%' }}>
        <AdaptiveLayoutShell
          isMobile={isMobile}
          isFullWidthRoute={isFullWidthRoute}
          isCollapsed={isCollapsed}
          onToggleCollapsed={toggleCollapsed}
          path={path}
          title={title}
          mobileTitle={mobileTitle}
        >
          {children}
        </AdaptiveLayoutShell>
      </Box>

      {/* Notification Center Drawer */}
      <Suspense fallback={null}>
        <NotificationCenter />
      </Suspense>

      {/* Promo Banner Modal */}
      <PromoBanner
        imageUrl={config.imageUrl}
        text={config.text}
        header={config.header}
        footer={config.footer}
        callToActionUrl={config.callToActionUrl}
        callToActionText={config.callToActionText}
        onDismiss={dismissBanner}
        isVisible={isBannerVisible}
      />

      {switchOverlay}
    </ThemeProvider>
  );
};

export default Layout;
